import storeJsEngine from 'store/src/store-engine'
import localforage from 'localforage'
import expirePlugin from 'store/plugins/expire'
import updatePlugin from 'store/plugins/update'

// IndexedDB 인스턴스 생성
const idb = localforage.createInstance({
  name: 'LinkMeMine_SE',
  version: 1.0,
  storeName: 'keyvaluepairs',
  description: 'LinkMeMine SE Web Manager Storage',
  driver: localforage.INDEXEDDB,
})

// 메모리 캐시
class MemoryCache {
  constructor() {
    this._cache = new Map()
  }
  get(key) { return this._cache.get(key) }
  set(key, value) { this._cache.set(key, value) }
  delete(key) { return this._cache.delete(key) }
  clear() { this._cache.clear() }
  forEach(fn) { this._cache.forEach(fn) }
  get size() { return this._cache.size }
}

const cache = new MemoryCache()

// 초기화 (앱 시작 시 자동 실행)
const initPromise = (async () => {
  try {
    if (!window.indexedDB) {
      throw new Error('IndexedDB not supported in this browser')
    }
    await migrateFromLocalStorage()
    const keys = await idb.keys()
    await Promise.all(
      keys.map(async (key) => {
        const value = await idb.getItem(key)
        cache.set(key, value)
      }),
    )
    return true
  } catch (err) {
    console.error('[Store] 초기화 실패:', err)
    cache.clear()
    return false
  }
})()

// localStorage → IndexedDB 마이그레이션
async function migrateFromLocalStorage() {
  try {
    const MIGRATION_KEY = '__lmm_se_migrated_v2__'

    // 이미 마이그레이션 했는지 확인
    if (localStorage.getItem(MIGRATION_KEY)) {
      return
    }

    const keys = Object.keys(localStorage)

    if (keys.length === 0) {
      localStorage.setItem(MIGRATION_KEY, new Date().toISOString())
      return
    }

    for (const key of keys) {
      try {
        const value = localStorage.getItem(key)
        const parsedValue = (() => {
          try { return JSON.parse(value) }
          catch { return value }
        })()
        await idb.setItem(key, parsedValue)
      } catch (err) {
        console.warn(`[Store] "${key}" 마이그레이션 실패:`, err)
      }
    }

    localStorage.setItem(MIGRATION_KEY, new Date().toISOString())
  } catch (err) {
    // localStorage 접근 불가 시 마이그레이션 스킵 (private browsing 등)
    console.warn('[Store] localStorage 접근 불가 - 마이그레이션 스킵')
  }
}

// Custom Storage Driver (store.js 인터페이스 준수)
const indexedDBStorageDriver = {
  read(key) {
    return cache.get(key)
  },
  write(key, value) {
    cache.set(key, value)
    idb.setItem(key, value).catch(err => {
      console.error(`[Store] "${key}" IndexedDB 저장 실패:`, err)
    })
  },
  each(fn) {
    cache.forEach((value, key) => fn(value, key))
  },
  remove(key) {
    cache.delete(key)
    idb.removeItem(key).catch(err => {
      console.error(`[Store] "${key}" IndexedDB 삭제 실패:`, err)
    })
  },
  clearAll() {
    cache.clear()
    idb.clear().catch(err => {
      console.error('[Store] IndexedDB 비우기 실패:', err)
    })
  },
}

// Store 인스턴스 생성
const store = storeJsEngine.createStore([indexedDBStorageDriver])
store.addPlugin(expirePlugin)
store.addPlugin(updatePlugin)

// 추가 메서드
store.waitForInitialization = () => initPromise

store.flush = async () => {
  const promises = []
  cache.forEach((value, key) => {
    promises.push(idb.setItem(key, value))
  })
  await Promise.all(promises)
}

store.reset = async () => {
  cache.clear()
  await idb.clear()
}

// Session Storage 래퍼
const session = {
  get(key) {
    const value = sessionStorage.getItem(key)
    if (!value) return null
    try { return JSON.parse(value) }
    catch { return value }
  },
  set(key, value) {
    const serialized = typeof value === 'string' ? value : JSON.stringify(value)
    sessionStorage.setItem(key, serialized)
  },
  remove(key) {
    sessionStorage.removeItem(key)
  },
  clear() {
    sessionStorage.clear()
  },
}

export default store
export { session, store }
